import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ConflictService, ConflictCheck, ConflictResult, ConflictSearchCriteria } from '../../../services/conflicts/conflict.service';
import { AlertService } from '../../../services/common/alert.service';

@Component({
  selector: 'app-conflict-check',
  templateUrl: './conflict-check.component.html',
  styleUrls: ['./conflict-check.component.css'],
})
export class ConflictCheckComponent implements OnInit {
  // Search criteria
  searchCriteria: ConflictSearchCriteria = {
    name: '',
    ssn: '',
    ein: '',
    phone: '',
    email: '',
    notify: false
  };

  // Search state
  searching: boolean = false;
  searched: boolean = false;

  // Results
  currentCheck: ConflictCheck | null = null;
  recentChecks: ConflictCheck[] = [];

  // Loading states
  loadingRecent: boolean = false;

  constructor(
    private conflictService: ConflictService,
    private alertService: AlertService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadRecentChecks();
  }

  loadRecentChecks(): void {
    this.loadingRecent = true;
    this.conflictService.getRecentChecks()
      .then((response) => {
        this.recentChecks = response.recent_checks || [];
        this.loadingRecent = false;
      })
      .catch((error) => {
        console.error('Error loading recent checks:', error);
        this.loadingRecent = false;
      });
  }

  search(): void {
    // Validate that at least one search criterion is provided
    if (!this.searchCriteria.name && !this.searchCriteria.ssn &&
        !this.searchCriteria.ein && !this.searchCriteria.phone &&
        !this.searchCriteria.email) {
      this.alertService.alert_status_danger('Please enter at least one search criterion');
      return;
    }

    this.searching = true;
    this.searched = false;
    this.currentCheck = null;

    this.conflictService.search(this.searchCriteria)
      .then((response) => {
        this.searching = false;
        this.searched = true;
        if (response.success) {
          this.currentCheck = response.conflict_check;
          if (this.currentCheck && this.currentCheck.conflicts_found > 0) {
            this.alertService.alert_status_warning(
              `Found ${this.currentCheck.conflicts_found} potential conflict(s)`
            );
          } else {
            this.alertService.alert_status_success('No conflicts found');
          }
          // Refresh recent checks
          this.loadRecentChecks();
        } else {
          this.alertService.alert_status_danger(response.error || 'Search failed');
        }
      })
      .catch((error) => {
        this.searching = false;
        this.searched = true;
        console.error('Conflict search error:', error);
        this.alertService.alert_status_danger('Error performing conflict search');
      });
  }

  clearSearch(): void {
    this.searchCriteria = {
      name: '',
      ssn: '',
      ein: '',
      phone: '',
      email: '',
      notify: false
    };
    this.searched = false;
    this.currentCheck = null;
  }

  viewCheck(check: ConflictCheck): void {
    this.conflictService.getConflictCheck(check.id)
      .then((response) => {
        this.currentCheck = response;
        this.searched = true;
      })
      .catch((error) => {
        console.error('Error loading conflict check:', error);
        this.alertService.alert_status_danger('Error loading conflict check details');
      });
  }

  sendNotification(): void {
    if (!this.currentCheck) return;

    this.conflictService.notify(this.currentCheck.id)
      .then((response) => {
        if (response.success) {
          this.alertService.alert_status_success(response.message || 'Notification sent');
        } else {
          this.alertService.alert_status_danger(response.error || 'Failed to send notification');
        }
      })
      .catch((error) => {
        console.error('Error sending notification:', error);
        this.alertService.alert_status_danger('Error sending notification');
      });
  }

  goToMatter(matterId: number): void {
    this.router.navigate(['/matters', matterId]);
  }

  goToContact(contactId: number): void {
    this.router.navigate(['/contacts', contactId]);
  }

  getRolesBadgeClass(roles: string[]): string {
    if (!roles || roles.length === 0) return 'badge-secondary';

    const hasClient = roles.some(r => r.toLowerCase() === 'client');
    const hasOpposing = roles.some(r =>
      r.toLowerCase().includes('defendant') ||
      r.toLowerCase().includes('plaintiff') ||
      r.toLowerCase().includes('opposing')
    );

    if (hasClient) return 'badge-success';
    if (hasOpposing) return 'badge-danger';
    return 'badge-info';
  }
}
